



















































































import Vue from 'vue'
import { DomEvent } from 'leaflet'
import TagsComboBox from './TagsComboBox.vue'
import ComboBox from './ComboBox.vue'
import Toggle from './Toggle.vue'

const FILTER_DEBOUNCE_MS = 1000

function pad (n: number) {
  return n < 10 ? '0' + n : String(n)
}

export function toDatetimeLocalValue (date: Date) : string {
  return [
    date.getFullYear(),
    '-',
    pad(date.getMonth() + 1),
    '-',
    pad(date.getDate()),
    'T',
    pad(date.getHours()),
    ':',
    pad(date.getMinutes())
  ].join('')
}

function splitDatetimeLocal (value: string) : { day: string, time: string } {
  if (!value || value.indexOf('T') < 0) {
    return { day: '', time: '' }
  }
  const parts = value.split('T')
  return { day: parts[0] || '', time: (parts[1] || '').slice(0, 5) }
}

function combineDatetimeLocal (day: string, time: string) : string {
  if (!day) {
    return ''
  }
  const safeTime = time || '00:00'
  return `${day}T${safeTime}`
}

export function defaultScheduleDatetimeWindow () : { from: string, to: string } {
  const from = new Date()
  from.setSeconds(0, 0)
  from.setMinutes(0)
  const to = new Date(from.getTime())
  to.setHours(23, 59, 0, 0)
  return { from: toDatetimeLocalValue(from), to: toDatetimeLocalValue(to) }
}

function ensureOrdered (fromValue: string, toValue: string) : { from: string, to: string } {
  const from = fromValue ? new Date(fromValue) : new Date()
  let to = toValue ? new Date(toValue) : new Date(from.getTime())
  if (isNaN(from.getTime())) {
    const defaults = defaultScheduleDatetimeWindow()
    return defaults
  }
  if (isNaN(to.getTime()) || to < from) {
    to = new Date(from.getTime())
    to.setHours(23, 59, 0, 0)
  }
  // Keep same calendar day
  if (
    from.getFullYear() !== to.getFullYear() ||
    from.getMonth() !== to.getMonth() ||
    from.getDate() !== to.getDate()
  ) {
    to = new Date(from.getTime())
    to.setHours(23, 59, 0, 0)
  }
  return { from: toDatetimeLocalValue(from), to: toDatetimeLocalValue(to) }
}

export default Vue.extend({
  components: { TagsComboBox, Toggle },
  data () {
    return {
      filterDebounceId: null as null | number
    }
  },
  computed: {
    selectedTags (): any[] {
      return this.$store.state.selectedTags || []
    },
    radiusKm (): number { return this.$store.state.scheduleRadiusKm },
    showAllPlaces (): boolean { return this.$store.state.scheduleShowAllPlaces },
    datetimeFrom (): string { return this.$store.state.scheduleDatetimeFrom },
    datetimeTo (): string { return this.$store.state.scheduleDatetimeTo },
    dayValue (): string {
      return splitDatetimeLocal(this.datetimeFrom).day || splitDatetimeLocal(this.datetimeTo).day
    },
    timeFromValue (): string {
      return splitDatetimeLocal(this.datetimeFrom).time || '00:00'
    },
    timeToValue (): string {
      return splitDatetimeLocal(this.datetimeTo).time || '23:59'
    }
  },
  mounted () {
    this.$store.dispatch('hydrateScheduleTagsFromRoute').finally(() => {
      const fromUrl = !!(this.$store.state.scheduleDatetimeFrom && this.$store.state.scheduleDatetimeTo)
      if (!fromUrl) {
        const windowDefaults = defaultScheduleDatetimeWindow()
        this.$store.commit('setScheduleDatetimeFrom', windowDefaults.from)
        this.$store.commit('setScheduleDatetimeTo', windowDefaults.to)
      } else {
        const aligned = ensureOrdered(
          this.$store.state.scheduleDatetimeFrom,
          this.$store.state.scheduleDatetimeTo
        )
        this.$store.commit('setScheduleDatetimeFrom', aligned.from)
        this.$store.commit('setScheduleDatetimeTo', aligned.to)
        this.$store.commit('setScheduleRadiusKm', this.$store.state.scheduleRadiusKm)
        this.$store.commit('setScheduleShowAllPlaces', this.$store.state.scheduleShowAllPlaces)
      }
      this.scheduleEmitFiltersChanged()
    })
    this.$nextTick(() => {
      const tags = this.$refs.tagsComboBox as InstanceType<typeof TagsComboBox> | undefined
      const elements: HTMLElement[] = [
        tags && tags.$el as HTMLElement,
        tags && tags.$refs.comboBox && (tags.$refs.comboBox as InstanceType<typeof ComboBox>).listElement,
        this.$refs.dayInput as HTMLElement,
        this.$refs.timeRow as HTMLElement,
        this.$refs.radiusRow as HTMLElement
      ].filter(Boolean) as HTMLElement[]
      elements.forEach(element => {
        DomEvent.disableClickPropagation(element)
        DomEvent.disableScrollPropagation(element)
      })
    })
  },
  beforeDestroy () {
    if (this.filterDebounceId) {
      clearTimeout(this.filterDebounceId)
    }
  },
  methods: {
    scheduleEmitFiltersChanged () {
      if (this.filterDebounceId) {
        clearTimeout(this.filterDebounceId)
      }
      this.filterDebounceId = window.setTimeout(() => {
        this.filterDebounceId = null
        this.$emit('filters-changed')
      }, FILTER_DEBOUNCE_MS)
    },
    commitWindow (from: string, to: string) {
      const aligned = ensureOrdered(from, to)
      this.$store.commit('setScheduleDatetimeFrom', aligned.from)
      this.$store.commit('setScheduleDatetimeTo', aligned.to)
      this.scheduleEmitFiltersChanged()
    },
    onSelectedTags (tags: any[]) {
      this.$store.commit('setSelectedTags', tags || [])
      this.scheduleEmitFiltersChanged()
    },
    onRadius (e: Event) {
      if (this.showAllPlaces) {
        return
      }
      this.$store.commit('setScheduleRadiusKm', (e.target as HTMLInputElement).value)
      this.scheduleEmitFiltersChanged()
    },
    onShowAllPlaces (value: boolean) {
      this.$store.commit('setScheduleShowAllPlaces', value)
      this.scheduleEmitFiltersChanged()
    },
    onDay (e: Event) {
      const day = (e.target as HTMLInputElement).value
      this.commitWindow(
        combineDatetimeLocal(day, this.timeFromValue),
        combineDatetimeLocal(day, this.timeToValue)
      )
    },
    onTimeFrom (e: Event) {
      const time = (e.target as HTMLInputElement).value
      this.commitWindow(
        combineDatetimeLocal(this.dayValue, time),
        combineDatetimeLocal(this.dayValue, this.timeToValue)
      )
    },
    onTimeTo (e: Event) {
      const time = (e.target as HTMLInputElement).value
      this.commitWindow(
        combineDatetimeLocal(this.dayValue, this.timeFromValue),
        combineDatetimeLocal(this.dayValue, time)
      )
    }
  }
})
